import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Check, PhoneCall, Calendar, ShoppingBag, CreditCard, FileText, Bell, Globe, Bot, Settings, Mail } from "lucide-react";
import PublicHeader from "@/components/PublicHeader";
import PublicFooter from "@/components/PublicFooter";
import LandingSplash from "@/components/LandingSplash";
import SEO from "@/components/SEO";
import QuoteEmailModal from "@/components/QuoteEmailModal";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { useI18n } from "@/contexts/I18nContext";
import { api } from "@/lib/api";
import { VERTICAL_IMAGES } from "@/lib/verticalImages";

const MODULE_ICONS = {
  M1: Settings, M2: PhoneCall, M3: Bot, M4: Calendar, M5: ShoppingBag,
  M6: CreditCard, M7: FileText, M8: Bell, M9: Globe,
};

export default function Landing() {
  const { t, lang } = useI18n();
  const [modules, setModules] = useState([]);
  const [verticals, setVerticals] = useState([]);
  const [selected, setSelected] = useState(new Set(["M1", "M9"]));
  const [quoteOpen, setQuoteOpen] = useState(false);
  const [loadingCatalog, setLoadingCatalog] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const [m, v] = await Promise.all([
          api.get("/public/modules"),
          api.get("/public/verticals"),
        ]);
        setModules(m.data.modules || []);
        setVerticals(v.data.verticals || []);
      } catch (e) {
        /* noop */
      } finally {
        setLoadingCatalog(false);
      }
    })();
  }, []);

  const total = useMemo(() => {
    return modules
      .filter((m) => selected.has(m.key))
      .reduce((acc, m) => acc + (m.price_monthly_eur || 0), 0);
  }, [selected, modules]);

  const toggle = (key) => {
    const next = new Set(selected);
    if (next.has(key)) {
      if (["M1", "M9"].includes(key)) return;
      next.delete(key);
    } else {
      next.add(key);
    }
    setSelected(next);
  };

  return (
    <div className="min-h-screen bg-background">
      <LandingSplash />
      <SEO
        title={lang === "fr" ? "Disponible 24/7 sans embaucher" : "Available 24/7 without hiring"}
        description={t("landing.heroSubtitle")}
        url={typeof window !== "undefined" ? window.location.origin : ""}
        lang={lang}
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "SoftwareApplication",
          name: "AI Commerce OS",
          applicationCategory: "BusinessApplication",
          operatingSystem: "Web",
          offers: { "@type": "Offer", price: "9", priceCurrency: "EUR" },
        }}
      />
      <PublicHeader />

      {/* HERO */}
      <section className="relative overflow-hidden border-b border-border">
        <div className="mx-auto grid max-w-7xl items-center gap-12 px-4 py-20 md:grid-cols-12 md:px-8 md:py-32">
          <div className="md:col-span-7">
            <div className="flex flex-wrap gap-2" data-testid="hero-trust-badges">
              <span className="rounded-sm bg-secondary px-2.5 py-1 text-[10px] font-semibold uppercase tracking-wide text-foreground/80">
                🇫🇷 Hébergé en France
              </span>
              <span className="rounded-sm bg-secondary px-2.5 py-1 text-[10px] font-semibold uppercase tracking-wide text-foreground/80">
                RGPD
              </span>
              <span className="rounded-sm bg-secondary px-2.5 py-1 text-[10px] font-semibold uppercase tracking-wide text-foreground/80">
                Sans engagement
              </span>
            </div>
            <span className="overline mt-4 block" data-testid="hero-overline">
              SaaS modulaire · 9 modules · Multi-tenant
            </span>
            <h1 className="mt-6 font-display text-4xl font-extrabold leading-[1.05] tracking-tighter text-balance sm:text-5xl lg:text-6xl">
              {t("landing.heroTitle")}
            </h1>
            <p className="mt-6 max-w-2xl text-base text-muted-foreground sm:text-lg">
              {t("landing.heroSubtitle")}
            </p>
            <div className="mt-10 flex flex-col gap-3 sm:flex-row">
              <Link to="/register">
                <Button size="lg" className="group rounded-md" data-testid="hero-cta-primary">
                  {t("landing.heroCta")}
                  <ArrowRight className="ml-2 h-4 w-4 transition-transform group-hover:translate-x-1" />
                </Button>
              </Link>
              <Link to="/pricing#roi-calculator">
                <Button
                  size="lg"
                  variant="outline"
                  className="group rounded-md border-emerald-500 text-emerald-700 hover:bg-emerald-50 dark:text-emerald-400 dark:hover:bg-emerald-950/30"
                  data-testid="hero-cta-roi"
                >
                  Calculer mon ROI
                  <ArrowRight className="ml-2 h-4 w-4 transition-transform group-hover:translate-x-1" />
                </Button>
              </Link>
              <Link to="/login">
                <Button size="lg" variant="outline" className="rounded-md" data-testid="hero-cta-secondary">
                  {t("common.login")}
                </Button>
              </Link>
            </div>
            <div className="mt-12 flex flex-wrap gap-6 text-xs text-muted-foreground">
              <span className="flex items-center gap-2"><Check className="h-3 w-3" /> {t("landing.heroBullet1")}</span>
              <span className="flex items-center gap-2"><Check className="h-3 w-3" /> {t("landing.heroBullet2")}</span>
              <span className="flex items-center gap-2"><Check className="h-3 w-3" /> {t("landing.heroBullet3")}</span>
            </div>
          </div>
          <div className="relative md:col-span-5">
            <div className="relative aspect-[4/5] w-full overflow-hidden rounded-md border border-border">
              <img
                src="https://images.unsplash.com/photo-1521017432531-fbd92d768814?q=80&w=1200"
                alt="Assistant vocal IA en train de traiter un appel"
                className="h-full w-full object-cover"
                loading="lazy"
              />
              <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/80 via-black/25 to-transparent p-6">
                <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-white/80">{t("landing.liveCallsBadge")}</div>
                <div
                  className="mt-3 flex items-center gap-3 rounded-md bg-black/60 p-3 backdrop-blur-sm"
                  data-testid="hero-live-call-card"
                >
                  <span className="relative flex h-2.5 w-2.5 shrink-0">
                    <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-400 opacity-75" />
                    <span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-emerald-500" />
                  </span>
                  <div className="min-w-0 flex-1">
                    <div className="truncate text-xs font-semibold text-white">{t("landing.heroLiveCallTitle")}</div>
                    <div className="mt-0.5 truncate text-[10px] text-white/60">{t("landing.heroLiveCallSub")}</div>
                  </div>
                  <span className="shrink-0 text-[10px] font-bold uppercase tracking-widest text-[hsl(var(--accent))]">
                    {t("landing.heroLiveCallBadge")}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* MODULES BENTO */}
      <section className="border-b border-border bg-secondary/40">
        <div className="mx-auto max-w-7xl px-4 py-20 sm:px-8 md:py-28">
          <div className="max-w-2xl">
            <span className="overline">{t("landing.moduleSectionLabel")}</span>
            <h2 className="mt-4 font-display text-3xl font-bold tracking-tight sm:text-4xl">
              {t("landing.moduleSectionTitle")}
            </h2>
            <p className="mt-4 text-muted-foreground">{t("landing.moduleSectionBody")}</p>
          </div>
          <div className="mt-12 grid grid-cols-1 gap-px overflow-hidden rounded-md border border-border bg-border sm:grid-cols-2 lg:grid-cols-3">
            {loadingCatalog && Array.from({ length: 6 }).map((_, idx) => (
              <div key={idx} className="flex flex-col gap-4 bg-background p-6" data-testid="module-card-skeleton">
                <div className="flex items-center justify-between">
                  <Skeleton className="h-9 w-9" />
                  <Skeleton className="h-4 w-10" />
                </div>
                <div>
                  <Skeleton className="h-5 w-3/5" />
                  <Skeleton className="mt-3 h-4 w-full" />
                  <Skeleton className="mt-2 h-4 w-4/5" />
                </div>
              </div>
            ))}
            {modules.map((m, idx) => {
              const Icon = MODULE_ICONS[m.key] || Settings;
              return (
                <article
                  key={m.key}
                  className="group flex flex-col gap-4 bg-background p-6 transition-all duration-200 hover:bg-secondary"
                  data-testid={`module-card-${m.key}`}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="flex h-9 w-9 items-center justify-center border border-border">
                        <Icon className="h-4 w-4" />
                      </div>
                      <span className="font-mono text-xs uppercase tracking-widest text-muted-foreground">{m.key}</span>
                    </div>
                    <span className="font-display text-sm font-bold">
                      {m.price_monthly_eur > 0 ? `+${m.price_monthly_eur}€` : "incl."}
                    </span>
                  </div>
                  <div>
                    <h3 className="font-display text-lg font-bold">
                      {lang === "fr" ? m.name_fr : m.name_en}
                    </h3>
                    <p className="mt-2 text-sm text-muted-foreground">
                      {lang === "fr" ? m.description_fr : m.description_en}
                    </p>
                  </div>
                  {m.required && (
                    <Badge variant="secondary" className="w-fit rounded-sm font-mono text-[10px] uppercase tracking-widest">
                      Toujours actif
                    </Badge>
                  )}
                </article>
              );
            })}
          </div>
        </div>
      </section>

      {/* VERTICALS */}
      <section className="border-b border-border">
        <div className="mx-auto max-w-7xl px-4 py-20 sm:px-8 md:py-28">
          <div className="max-w-2xl">
            <span className="overline">{t("landing.verticalsLabel")}</span>
            <h2 className="mt-4 font-display text-3xl font-bold tracking-tight sm:text-4xl">
              {t("landing.verticalsTitle")}
            </h2>
          </div>
          <div className="mt-12 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-5">
            {loadingCatalog && Array.from({ length: 5 }).map((_, idx) => (
              <Skeleton key={idx} className="aspect-[3/4] w-full rounded-none border border-border" data-testid="vertical-card-skeleton" />
            ))}
            {verticals.map((v) => (
              <div
                key={v.key}
                className="group relative aspect-[3/4] overflow-hidden border border-border"
                data-testid={`vertical-card-${v.key}`}
              >
                <img
                  src={VERTICAL_IMAGES[v.key] || VERTICAL_IMAGES.RESTAURANT}
                  alt={v.name_fr}
                  className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                  loading="lazy"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent" />
                <div className="absolute inset-x-0 bottom-0 p-4">
                  <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-white/70">
                    {(v.default_modules || []).length} modules
                  </div>
                  <h3 className="mt-1 font-display text-xl font-bold text-white">
                    {lang === "fr" ? v.name_fr : v.name_en}
                  </h3>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* PRICING CALCULATOR */}
      <section className="border-b border-border bg-secondary/40">
        <div className="mx-auto max-w-7xl px-4 py-20 sm:px-8 md:py-28">
          <div className="grid gap-12 md:grid-cols-12">
            <div className="md:col-span-5">
              <span className="overline">{t("landing.pricingLabel")}</span>
              <h2 className="mt-4 font-display text-3xl font-bold tracking-tight sm:text-4xl">
                {t("landing.pricingTitle")}
              </h2>
              <div className="mt-10 border border-border bg-background p-6">
                <div className="overline">{t("common.totalMonth")}</div>
                <div className="mt-2 font-display text-5xl font-extrabold tracking-tighter" data-testid="pricing-total">
                  {total}€
                </div>
                <p className="mt-4 text-sm text-muted-foreground">
                  {t("landing.modulesSelected").split("{s}").join(selected.size > 1 ? "s" : "").replace("{count}", selected.size)}
                </p>
                <Link to="/register" className="mt-6 inline-block">
                  <Button className="rounded-md" data-testid="pricing-cta">
                    {t("landing.heroCta")}
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                </Link>
                <Button
                  variant="outline"
                  className="mt-3 w-full rounded-md sm:w-auto sm:ml-3"
                  onClick={() => setQuoteOpen(true)}
                  data-testid="quote-open-btn"
                >
                  <Mail className="mr-2 h-4 w-4" />
                  Recevoir mon devis par e-mail
                </Button>
              </div>
            </div>
            <div className="md:col-span-7">
              <div className="overflow-hidden border border-border bg-background">
                {loadingCatalog && Array.from({ length: 6 }).map((_, idx) => (
                  <div key={idx} className="flex items-center justify-between border-b border-border p-4 last:border-b-0">
                    <Skeleton className="h-4 w-40" />
                    <Skeleton className="h-5 w-9" />
                  </div>
                ))}
                {modules.map((m) => {
                  const isMandatory = ["M1", "M9"].includes(m.key);
                  const isSelected = selected.has(m.key);
                  return (
                    <div
                      key={m.key}
                      className="flex items-center justify-between border-b border-border p-4 last:border-b-0"
                    >
                      <div className="flex items-center gap-4">
                        <span className="font-mono text-xs text-muted-foreground">{m.key}</span>
                        <div>
                          <div className="font-medium">{lang === "fr" ? m.name_fr : m.name_en}</div>
                          {isMandatory && (
                            <div className="text-[10px] uppercase tracking-widest text-muted-foreground">
                              Toujours inclus
                            </div>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center gap-4">
                        <span className="font-mono text-sm tabular-nums">
                          {m.price_monthly_eur > 0 ? `+${m.price_monthly_eur}€` : "0€"}
                        </span>
                        <Switch
                          checked={isSelected}
                          disabled={isMandatory}
                          onCheckedChange={() => toggle(m.key)}
                          data-testid={`pricing-toggle-${m.key}`}
                        />
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA FINAL */}
      <section className="border-b border-border">
        <div className="mx-auto max-w-7xl px-4 py-24 text-center sm:px-8">
          <h2 className="mx-auto max-w-3xl font-display text-4xl font-extrabold tracking-tighter sm:text-5xl">
            {t("landing.ctaFinalTitle")}
          </h2>
          <p className="mx-auto mt-6 max-w-xl text-muted-foreground">{t("landing.ctaFinalBody")}</p>
          <div className="mt-10 flex justify-center gap-3">
            <Link to="/register">
              <Button size="lg" className="rounded-md" data-testid="cta-final-primary">
                {t("landing.heroCta")}
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      <PublicFooter />

      <QuoteEmailModal
        open={quoteOpen}
        onOpenChange={setQuoteOpen}
        modules={selected}
        total={total}
      />
    </div>
  );
}
